import { useIntl } from '@umijs/max';
import { Button, message, notification } from 'antd';
import { createLogger } from '@/utils/logger';
import defaultSettings from '../config/defaultSettings';

const logger = createLogger('Global');

const { pwa } = defaultSettings;

const clearCache = () => {
  // remove all caches
  if (window.caches) {
    caches
      .keys()
      .then((keys) => {
        keys.forEach((key) => {
          caches.delete(key);
        });
      })
      .catch((e) => logger.warn('清除缓存失败', e));
  }
};

/**
 * 强制卸载浏览器中遗留的 Service Worker。
 *
 * 历史背景：本项目曾经短暂启用过 PWA，把基于 workbox 的 SW 注册到了 localhost:8000
 * （以及部分历史部署域名）。后来 PWA 关闭后，代码不再注册 SW，但浏览器里的旧 SW
 * 仍会以 control 状态存活，并按它当初的路由策略拦截请求 —— 典型表现就是
 * 「退出登录后再次提交登录表单时，POST /v1/auth/login 直接卡住、Network 面板里
 * 看不到任何请求」（SW 内部 `fetch` handler 永远不 respondWith / 报错）。
 *
 * 重要：仅调用 `unregister()` 不能让当前页面立刻脱离 SW 控制 —— SW 对当前
 * document 的控制要等到本次会话结束才会释放。所以需要在确实检测到注册时
 * 等 unregister 完成后再 `location.reload()`，让下一次加载彻底无 SW。
 *
 * 触发条件刻意放宽到 HTTP/HTTPS 都生效，因为本地 dev (http://localhost) 也会踩。
 */
const cleanupStaleServiceWorker = (): void => {
  if (!('serviceWorker' in navigator)) return;
  const { serviceWorker } = navigator;
  if (!serviceWorker.getRegistrations) return;

  serviceWorker
    .getRegistrations()
    .then((registrations) => {
      if (registrations.length === 0) return;
      logger.warn(
        '检测到残留的 Service Worker，正在卸载并强制刷新一次以脱离其控制',
        { count: registrations.length },
      );
      return Promise.all(registrations.map((reg) => reg.unregister())).then(
        () => {
          // 也清掉残留的 Cache Storage 内容，避免下一次加载继续读到旧缓存
          if (window.caches) {
            return caches
              .keys()
              .then((keys) =>
                Promise.all(keys.map((k) => caches.delete(k))).then(
                  () => undefined,
                ),
              );
          }
          return undefined;
        },
      );
    })
    .then((didCleanup) => {
      if (didCleanup !== undefined) {
        // 给浏览器一帧时间把 unregister 状态落盘，再 reload
        setTimeout(() => window.location.reload(), 0);
      }
    })
    .catch((err) => {
      logger.warn('卸载残留 Service Worker 失败', err);
    });
};

// if pwa is true
if (pwa) {
  // Notify user if offline now
  window.addEventListener('sw.offline', () => {
    message.warning(useIntl().formatMessage({ id: 'app.pwa.offline' }));
  });

  // Pop up a prompt on the page asking the user if they want to use the latest version
  window.addEventListener('sw.updated', (event: Event) => {
    const e = event as CustomEvent;
    const reloadSW = async () => {
      // Check if there is sw whose state is waiting in ServiceWorkerRegistration
      // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration
      const worker = e.detail?.waiting;
      if (!worker) {
        return true;
      }
      // Send skip-waiting event to waiting SW with MessageChannel
      try {
        await new Promise<void>((resolve, reject) => {
          const channel = new MessageChannel();
          let isResolved = false;
          const timeoutId = setTimeout(() => {
            if (!isResolved) {
              isResolved = true;
              channel.port1.close();
              // 超时后仍然继续执行，因为 skipWaiting 可能已经成功
              logger.warn('Service Worker skip-waiting 响应超时，继续执行');
              resolve();
            }
          }, 5000); // 5秒超时

          channel.port1.onmessage = (msgEvent) => {
            if (isResolved) return;
            isResolved = true;
            clearTimeout(timeoutId);
            channel.port1.close();
            if (msgEvent.data?.error) {
              reject(msgEvent.data.error);
            } else {
              resolve();
            }
          };

          try {
            worker.postMessage({ type: 'skip-waiting' }, [channel.port2]);
          } catch (error) {
            if (isResolved) return;
            isResolved = true;
            clearTimeout(timeoutId);
            channel.port1.close();
            // 发送消息失败时仍然继续执行
            logger.warn('Service Worker 消息发送失败，继续执行', error);
            resolve();
          }
        });
      } catch (error) {
        // 捕获所有错误，但仍然继续执行清理和刷新
        logger.warn('Service Worker skip-waiting 处理出错，继续执行', error);
      }

      clearCache();
      window.location.reload();
      return true;
    };
    const key = `open${Date.now()}`;
    const btn = (
      <Button
        type="primary"
        onClick={() => {
          notification.destroy(key);
          reloadSW();
        }}
      >
        {useIntl().formatMessage({ id: 'app.pwa.serviceworker.updated.ok' })}
      </Button>
    );
    notification.open({
      message: useIntl().formatMessage({ id: 'app.pwa.serviceworker.updated' }),
      description: useIntl().formatMessage({
        id: 'app.pwa.serviceworker.updated.hint',
      }),
      btn,
      key,
      onClose: async () => null,
    });
  });
} else {
  // PWA 关闭场景：兜底清掉历史遗留的 Service Worker（详见 cleanupStaleServiceWorker 注释）。
  cleanupStaleServiceWorker();
}
